import { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateFrenchToEnglish } from '../lib/translationDictionary';

const TRANSLATABLE_ATTRIBUTES = ['placeholder', 'aria-label', 'title', 'alt'] as const;
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'CANVAS']);

export default function GlobalDomTranslator() {
  const { language } = useLanguage();
  const originalTextRef = useRef<WeakMap<Text, string>>(new WeakMap());
  const originalAttributeRef = useRef<WeakMap<Element, Map<string, string>>>(new WeakMap());

  useEffect(() => {
    const root = document.documentElement;
    let isApplying = false;

    const translateValue = (value: string) => (language === 'en' ? translateFrenchToEnglish(value) : value);

    const shouldSkipTextNode = (node: Text) => {
      const parent = node.parentElement;
      if (!parent) return true;
      if (SKIPPED_TAGS.has(parent.tagName)) return true;
      if (parent.closest('[data-no-auto-translate="true"]')) return true;
      if (parent.closest('[contenteditable="true"]')) return true;
      return false;
    };

    const applyTextTranslation = (node: Text) => {
      if (shouldSkipTextNode(node)) return;
      const currentValue = node.nodeValue ?? '';
      if (!originalTextRef.current.has(node)) {
        originalTextRef.current.set(node, currentValue);
      }
      const originalValue = originalTextRef.current.get(node) ?? currentValue;
      const translatedValue = translateValue(originalValue);
      if (currentValue !== translatedValue) {
        node.nodeValue = translatedValue;
      }
    };

    const applyAttributeTranslation = (element: Element) => {
      if (element.closest('[data-no-auto-translate="true"]')) return;
      if (SKIPPED_TAGS.has(element.tagName)) return;

      let savedAttributes = originalAttributeRef.current.get(element);
      if (!savedAttributes) {
        savedAttributes = new Map<string, string>();
        originalAttributeRef.current.set(element, savedAttributes);
      }

      TRANSLATABLE_ATTRIBUTES.forEach((attributeName) => {
        const currentValue = element.getAttribute(attributeName);
        if (currentValue === null) return;

        if (!savedAttributes?.has(attributeName)) {
          savedAttributes?.set(attributeName, currentValue);
        }

        const originalValue = savedAttributes?.get(attributeName) ?? currentValue;
        const translatedValue = translateValue(originalValue);
        if (currentValue !== translatedValue) {
          element.setAttribute(attributeName, translatedValue);
        }
      });
    };

    const applyOnNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        applyTextTranslation(node as Text);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_NODE) {
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        applyAttributeTranslation(node as Element);
      }

      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      let currentTextNode = walker.nextNode();
      while (currentTextNode) {
        applyTextTranslation(currentTextNode as Text);
        currentTextNode = walker.nextNode();
      }

      if ('querySelectorAll' in node) {
        const selector = TRANSLATABLE_ATTRIBUTES.map((attr) => `[${attr}]`).join(', ');
        (node as ParentNode).querySelectorAll(selector).forEach((element) => applyAttributeTranslation(element));
      }
    };

    const applyTranslations = (node: Node) => {
      if (isApplying) return;
      isApplying = true;
      try {
        applyOnNode(node);
      } finally {
        isApplying = false;
      }
    };

    document.documentElement.lang = language;
    applyTranslations(root);

    const observer = new MutationObserver((mutations) => {
      if (isApplying) return;

      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          applyTranslations(mutation.target);
          continue;
        }

        if (mutation.type === 'attributes') {
          applyTranslations(mutation.target);
          continue;
        }

        mutation.addedNodes.forEach((addedNode) => applyTranslations(addedNode));
      }
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
    });

    return () => {
      observer.disconnect();
    };
  }, [language]);

  return null;
}
